<template>
  <div>
    <div v-show="pictures.length > 1" :class="$style.wrapper">
      <div v-if="isShow" :class="$style.photoPager">
        <p>{{ `${current + 1} / ${pictures.length}` }}</p>
      </div>
      <swiper-container ref="wrapperRef" init="false">
        <swiper-slide v-for="(picture, index) in pictures" :key="index" :data-swiper-slide-index="index">
          <div :class="$style.dialog">
            <div class="swiper-zoom-container" :class="$style.imageWrap">
              <img :src="dynamicImageUrl(picture.path, Math.min(picture.width, 1280))" :alt="picture.alt" :width="picture.width" :height="picture.height" :loading="current === index ? 'eager' : 'lazy'" />
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
    <div v-if="pictures.length === 1" :class="$style.wrapper">
      <div :class="$style.dialog">
        <div class="swiper-zoom-container" :class="$style.imageWrap">
          <img :src="dynamicImageUrl(pictures[0].path, Math.min(pictures[0].width, 1280))" :alt="pictures[0].alt" :width="pictures[0].width" :height="pictures[0].height" />
        </div>
      </div>
    </div>
    <transition name="fade">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="comment" :class="$style.dialogComment" v-html="linkify(comment)" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import type Swiper from 'swiper';
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { linkify } from '@/js/lib/helper/linkify';
import { dynamicImageUrl } from '@/js/lib/helper/url';

const props = withDefaults(
  defineProps<{
    pictures?: {
      path: string;
      alt: string;
      width: number;
      height: number;
      comment: string;
    }[];
    currentIndex?: number;
    isShowModal?: boolean;
    injectStyles?: string[];
  }>(),
  {
    pictures: () => [],
    currentIndex: 0,
    injectStyles: () => [],
  },
);
const emit = defineEmits<{
  (e: 'change', index: number): void;
}>();
const wrapperRef = ref();
const isMounted = ref(false);
const swiper = ref<Swiper>();
const current = ref(0);
const isShow = computed(() => props.isShowModal);
const comment = computed(() => props.pictures[current.value].comment);

onMounted(() => {
  isMounted.value = true;
});
onUnmounted(() => {
  swiper.value?.destroy();
});

watchEffect(() => {
  if (!swiper.value && isMounted.value && isShow.value && props.pictures.length > 1) {
    Object.assign(wrapperRef.value, {
      navigation: true,
      zoom: true,
      initialSlide: props.currentIndex,
      injectStyles: props.injectStyles,
    });

    wrapperRef.value.addEventListener('swiperprogress', (e: { detail: [Swiper, number] }) => {
      [swiper.value] = e.detail;
    });
    wrapperRef.value.addEventListener('swiperslidechange', () => {
      current.value = swiper.value!.realIndex;
      emit('change', current.value);
    });
    wrapperRef.value.initialize();
    // The number of slides is not enough for loop mode エラーを回避するための遅延処理
    setTimeout(() => {
      Object.assign(wrapperRef.value, {
        loop: true,
      });
    }, 500);
  }
});
</script>

<style module lang="scss">
.wrapper {
  aspect-ratio: 1 / 1;
  max-height: 100vh;
  max-height: 100dvh;
  max-width: 100vw;
  width: 100%;
}

.photoPager {
  left: 0;
  padding: 20px;
  pointer-events: none;
  position: fixed;
  text-align: center;
  top: 0;
  width: 100%;
  z-index: 100;

  @media (--sp) {
    padding: 12px;
  }

  p {
    background-color: var(--color-black-alpha40);
    border-radius: 20px;
    color: var(--color-white);
    display: inline-block;
    padding: 4px 10px;
  }
}

.dialog {
  background: var(--color-gray100) url('@/svgs/vue/loader.svg') no-repeat center center;

  .imageWrap {
    aspect-ratio: 1 / 1;

    img {
      height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }
}

.dialogComment {
  background-color: var(--color-black-alpha40);
  bottom: 0;
  color: var(--color-white);
  left: 50%;
  max-width: 100vh;
  padding: 20px;
  pointer-events: none;
  position: fixed;
  transform: translateX(-50%);
  white-space: pre-wrap;
  width: 100%;
  z-index: 10;

  @media (--sp) {
    font-size: 12px;
    left: 0;
    max-width: initial;
    padding: 10px;
    transform: none;
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
